/**
 * Putting the panels away.
 *
 * The owner's checklist is dismissed for this visit only: it is back at the
 * next sign-in until every step is done, which is what "reappears until
 * complete" means and what a browser session is for. A teacher's welcome
 * panel is dismissed for good — it explains the place once, and a panel that
 * came back would be nagging somebody who has already read it.
 *
 * Storage throws in a locked-down browser rather than returning null, so every
 * access is guarded; the panels simply show again, which is the safe failure.
 */
const CHECKLIST_KEY = (uid: string) => `spontar.backstage.checklist.dismissed.${uid}`;
const WELCOME_KEY = (uid: string) => `spontar.backstage.welcome.dismissed.${uid}`;

function read(storage: Storage | undefined, key: string): boolean {
  try {
    return storage?.getItem(key) === "1";
  } catch {
    return false;
  }
}

function write(storage: Storage | undefined, key: string, dismissed: boolean): void {
  try {
    if (dismissed) storage?.setItem(key, "1");
    else storage?.removeItem(key);
  } catch {
    // Not persisted. The panel shows again next time, which is harmless.
  }
}

const session = () => (typeof window === "undefined" ? undefined : window.sessionStorage);
const local = () => (typeof window === "undefined" ? undefined : window.localStorage);

export function isChecklistDismissed(uid: string): boolean {
  return read(session(), CHECKLIST_KEY(uid));
}

export function setChecklistDismissed(uid: string, dismissed: boolean): void {
  write(session(), CHECKLIST_KEY(uid), dismissed);
}

export function isWelcomeDismissed(uid: string): boolean {
  return read(local(), WELCOME_KEY(uid));
}

export function setWelcomeDismissed(uid: string, dismissed: boolean): void {
  write(local(), WELCOME_KEY(uid), dismissed);
}
